"use client";

import { useEffect, useState } from "react";
import { AppLink as Link } from "@/components/AppLink";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";
import { cleanMatricula } from "@/lib/solicitudHelpers";

interface SesionFast360Ui {
  activa?: boolean;
  solicitudId?: string;
  matricula?: string;
  desde?: string;
  /** uid del agente cuya cuenta de Fast360 usa el bot; null = cuenta de reserva. */
  uidAfectado?: string | null;
}

/**
 * Aviso: Fast360 solo admite una sesión por usuario. Si el bot está dentro
 * con la cuenta personal de un agente, solo a ese agente le avisamos; si usa
 * la cuenta de reserva compartida, el aviso es para todos.
 */
export function Fast360SesionBanner() {
  const { usuarioRPA } = useAuth();
  const [sesion, setSesion] = useState<SesionFast360Ui | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "sistema", "fast360UiSesion"),
      (snap) => {
        if (!snap.exists()) {
          setSesion(null);
          return;
        }
        const data = snap.data() as SesionFast360Ui;
        setSesion(data?.activa ? data : null);
      },
      () => setSesion(null),
    );
    return () => unsub();
  }, []);

  if (!sesion?.activa || !sesion.solicitudId) return null;
  if (sesion.uidAfectado && sesion.uidAfectado !== usuarioRPA?.uid) return null;

  const esTuCuenta = Boolean(
    sesion.uidAfectado && sesion.uidAfectado === usuarioRPA?.uid,
  );

  const mat = cleanMatricula(sesion.matricula) || sesion.solicitudId;

  return (
    <div
      role="status"
      className="shrink-0 px-4 py-2.5 bg-violet-700 text-white text-sm font-medium flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-violet-900 shadow-md z-40"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-200 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-violet-200" />
      </span>
      <span>
        Bot Fast360 trabajando en{" "}
        <Link
          href={`/emision/${sesion.solicitudId}`}
          className="underline font-bold hover:text-violet-100"
        >
          {mat}
        </Link>
        {" — "}
        {esTuCuenta ?
          "usa tu usuario de Fast360: no abras sesión hasta que termine." :
          "no abras sesión en Fast360 (una sola sesión activa)."}
      </span>
    </div>
  );
}
